import { useQuery } from '@tanstack/react-query';
import iziToast from 'izitoast';
import { FileIcon, LoaderPinwheelIcon, VectorSquareIcon } from 'lucide-react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FolderContent } from 'sbx-lib-ts';
import { Button } from 'src/components/ui/button';
import { AppAgentContext } from 'src/context/ContextProvider';
import useService from 'src/hooks/useService';
import { deleteFileService, getFolderFilesService, uploadFileService } from 'src/services/AssetService';
import { appActions } from 'src/store';
import { Retriever } from 'src/types/AiAgentV3';
import { syncVectorStore, SyncVectorStorePayload } from '../../../services/FolderFlowService';
import { ModalType } from '../../../types/Modal';
import { FileUpload } from '../FileUpload';
import ModalComponent from './index';
import { useTranslate } from 'sbx-crm-menu-ui';

export interface DocumentRetrieversProps {
    title: string;
    onClose: () => void;
    retriever: Retriever;
    agent: string;
}

export type DocumentRetrieversPropsWrapper = { type: ModalType.DOCUMENT_RETRIEVERS; data: DocumentRetrieversProps };

const KEY_NOT_SYNCRONIZE = 'not_syncronize';

export const DocumentRetrieversModal = ({ title, onClose, retriever, agent }: DocumentRetrieversProps) => {
    // const [sbxFiles, setSbxFiles] = useState<FolderContent[]>([]);
    const [namespace, setNamespace] = useState('');
    const [indexName, setIndexName] = useState('');
    const {
        state: {
            session: {
                ai_agent_config: { domain_id },
            },
        },
        dispatch,
    } = useContext(AppAgentContext);
    const { t } = useTranslate('agent');

    // const allowCreate = useMemo(() => {
    //     return grantPermission(DOCUMENT_V2_CREATE, permissions);
    // }, [permissions]);

    useEffect(() => {
        const name = retriever.name;
        const { params } = retriever;
        setNamespace(
            params?.vector_store_config?.namespace || `${domain_id}-${name}`.toLowerCase().replaceAll(/[_ ]/g, '-')
        );
        setIndexName(params?.index_name?.replaceAll(/[_ ]/g, '-') || `${domain_id}-${agent}`);
    }, [retriever, domain_id]);

    const onCloseProp = () => {
        if (localStorage.getItem(KEY_NOT_SYNCRONIZE)) {
            dispatch(
                appActions.openModal({
                    type: ModalType.CONFIRMATION,
                    data: {
                        title: t('not_synchronized'),
                        description: t('not_synchronized_confirm'),
                        onConfirm: () => {
                            dispatch(appActions.closeModal(ModalType.CONFIRMATION));
                            // const keys = sbxFiles.map((file) => file.key);
                            // deleteFilesAndFolder(keys, retriever.name, retriever.folder || '', domain_id).then();
                            onClose();
                            localStorage.removeItem(KEY_NOT_SYNCRONIZE);
                        },
                        onClose: () => dispatch(appActions.closeModal(ModalType.CONFIRMATION)),
                    },
                })
            );
        } else {
            onClose();
            // dispatch(actionModal.onClose({ key: keyModal }));
        }
    };

    return (
        <ModalComponent
            onClose={onCloseProp}
            className="bg-gradient-to-br from-purple-100 via-pink-100 to-blue-100 rounded-3xl shadow-2xl w-[90vw] max-w-[1300px]"
            title={title}
            open
        >
            {() => {
                return (
                    <div>
                        <div className="grid grid-cols-2 gap-4 mt-4">
                            {/* File Upload Section - Works independently from form */}
                            <div className="col-span-full">
                                <InputFilesAgent
                                    namespace={namespace}
                                    indexName={indexName}
                                    retriever={retriever}
                                    // getSbxFiles={setSbxFiles}
                                />
                            </div>
                        </div>
                    </div>
                );
            }}
        </ModalComponent>
    );
};

interface InputFilesAgentProps {
    retriever: Retriever;
    namespace: string;
    indexName: string;
    getSbxFiles?: (files: FolderContent[]) => void;
}

// interface FilesResponse {
//     success: true;
//     files: FolderContent[];
//     folderKey: string;
// }

export function InputFilesAgent({ retriever, getSbxFiles, namespace, indexName }: InputFilesAgentProps) {
    const {
        state: {
            session: {
                ai_agent_config: { domain_id = 0 },
            },
        },
    } = useContext(AppAgentContext);
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    //use query params
    const { id } = useParams<{
        id: string;
    }>();
    const { t: c } = useTranslate('common');
    const { t } = useTranslate('agent');

    const {
        data,
        refetch,
        isLoading: isLoadingFolders,
    } = useQuery({
        queryKey: ['folders', domain_id, retriever.folder],
        queryFn: async (): Promise<any | Record<'success', false>> => {
            const response = await getFolderFilesService(retriever.folder || '');
            if (response.success) {
                return {
                    success: true,
                    files: response.items,
                    folderKey: (response.folder as any)?.key,
                };
            }
            return { success: false };
        },
    });

    useEffect(() => {
        getSbxFiles?.(data?.success ? data?.files || [] : []);
    }, [data]);

    const { service: uploadFile, loading: uploadFileLoading } = useService(uploadFileService, [
        id,
        uploadedFiles,
        data,
    ]);

    const { service: syncVectorStoreService, loading: syncVectorStoreLoading } = useService(syncVectorStore, [
        domain_id,
    ]);

    // const { data: syncHistory, refetch: refetchSyncHistory } = useQuery<VectorStoreSyncHistory | null>({
    //     queryKey: ['vector-store-sync-history', domain_id, namespace],
    //     queryFn: async (): Promise<VectorStoreSyncHistory | null> => {
    //         if (!namespace) return null;
    //         const response = await getVectorStoreSyncHistory(domain_id!, namespace);
    //         if (response?.success && response?.items) {
    //             const items = response.items as unknown as VectorStoreSyncHistory[];
    //             if (Array.isArray(items) && items.length > 0) {
    //                 // Get the most recent sync
    //                 return items[0];
    //             }
    //         }
    //         return null;
    //     },
    //     enabled: !!namespace && !!domain_id,
    // });

    const handleDeleteFile = async (file: FolderContent): Promise<void> => {
        await deleteFileService(file.key);
        localStorage.setItem(KEY_NOT_SYNCRONIZE, 'true');
        refetch();
    };

    const handleUploadFiles = useCallback(async () => {
        if (data?.success && data.folderKey) {
            try {
                await uploadFile(data.folderKey, uploadedFiles);
                localStorage.setItem(KEY_NOT_SYNCRONIZE, 'true');
                refetch();
                setUploadedFiles([]);
                iziToast.success({ message: c('files_uploaded'), position: 'bottomLeft', timeout: 3000 });
            } catch (error: any) {
                iziToast.error({
                    message: error?.message || c('error_uploading_files'),
                    position: 'bottomLeft',
                    timeout: 3000,
                });
            }
        } else {
            // dispatch(
            //     appActions.openModal({
            //         type: ModalType.CONFIRMATION,
            //         data: {
            //             title: 'No Folder Found',
            //             description: 'No folder found for this agent, do you want to create one?',
            //             state: DataStates.DOCUMENT,
            //             onConfirm: async () => {
            //                 dispatch(appActions.onLoading({ type: DataStates.DOCUMENT, status: State.LOADING }));
            //                 const response = await createFolder(domainId!, key_retriever!);
            //                 // form.setValue('folder', (response.folder as any)?.key || '');
            //                 refetch();
            //                 dispatch(appActions.onLoading({ type: DataStates.DOCUMENT, status: State.SUCCESS }));
            //                 dispatch(appActions.closeModal(ModalType.CONFIRMATION));
            //             },
            //             onClose: () => dispatch(appActions.closeModal(ModalType.CONFIRMATION)),
            //         },
            //     })
            // );
        }
    }, [data, uploadedFiles]);

    const handleSyncVectorStore = useCallback(async () => {
        if (!data?.success || !data.folderKey) {
            iziToast.error({ message: t('no_folder_found_sync'), position: 'bottomLeft', timeout: 3000 });
            return;
        }

        const formValues = retriever;
        const payload: SyncVectorStorePayload = {
            provider: formValues.params?.provider || 'chroma_db',
            index_name: formValues.params?.index_name || indexName,
            folder: data.folderKey,
            vector_store_config: {
                k: formValues.params?.vector_store_config?.k || 4,
                namespace: formValues.params?.vector_store_config?.namespace || namespace,
                fetch_k: formValues.params?.vector_store_config?.fetch_k,
                chunk_size: formValues.params?.vector_store_config?.chunk_size || 4000,
                search_type: formValues.params?.vector_store_config?.search_type || 'mmr',
                chunk_overlap: formValues.params?.vector_store_config?.chunk_overlap || 50,
                lambda_mult: formValues.params?.vector_store_config?.lambda_mult,
                score_threshold: formValues.params?.vector_store_config?.score_threshold,
            },
        };

        try {
            const response = await syncVectorStoreService(payload);
            if (response?.success) {
                localStorage.removeItem(KEY_NOT_SYNCRONIZE);
                iziToast.success({
                    message: t('vector_store_sync'),
                    position: 'bottomLeft',
                    timeout: 3000,
                });
                // refetchSyncHistory();
            } else {
                iziToast.error({
                    message: response?.message || t('error_sync_vector_store'),
                    position: 'bottomLeft',
                    timeout: 3000,
                });
            }
        } catch (error: any) {
            iziToast.error({
                message: error?.message || t('error_sync_vector_store'),
                position: 'bottomLeft',
                timeout: 3000,
            });
        }
    }, [data, domain_id, syncVectorStoreService, retriever, indexName, namespace]);

    return (
        <div>
            <FileUpload
                files={uploadedFiles}
                sbxFiles={data?.success ? data?.files || [] : []}
                onFilesChange={setUploadedFiles}
                onDeleteFile={handleDeleteFile}
                label={
                    <div className="flex gap-2">
                        <div>
                            <p>
                                {t('upload_files_to_sync_vector_store')}{' '}
                                {!data?.success && !isLoadingFolders && (
                                    <span className="text-xs text-red-500">({t('no_folder_found')})</span>
                                )}
                            </p>
                            <p className="text-xs text-muted-foreground">
                                {c('max')} {10} {c('files')}, {10 * 1024 * 1024} {t('bytes_each')}
                            </p>
                        </div>

                        {data?.success && !!data?.files?.length && (
                            <div className="ml-auto mb-4 flex flex-col items-end gap-2">
                                {/* {syncHistory && (
                                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                        <ClockIcon className="h-3 w-3" />
                                        <span>Last sync: {new Date(syncHistory.timestamp).toLocaleString()}</span>
                                    </div>
                                )} */}
                                <Button
                                    type="button"
                                    variant="gradient"
                                    disabled={syncVectorStoreLoading}
                                    onClick={handleSyncVectorStore}
                                >
                                    {syncVectorStoreLoading ? (
                                        <LoaderPinwheelIcon className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                        <VectorSquareIcon className="w-[16px] h-[16px]" />
                                    )}{' '}
                                    <span className="leading-[1]">{t('sync_vector_store')}</span>{' '}
                                    {!namespace && (
                                        <span className="text-xs text-red-500">{t('namespace_required')}</span>
                                    )}
                                </Button>
                            </div>
                        )}
                    </div>
                }
                accept=".pdf, .txt"
                maxFiles={10}
            />
            {!!uploadedFiles.length && (
                <Button
                    type="button"
                    variant="gradient"
                    disabled={uploadFileLoading}
                    onClick={handleUploadFiles}
                    className="mt-4"
                >
                    {uploadFileLoading ? <LoaderPinwheelIcon className="mr-2 h-4 w-4 animate-spin" /> : <FileIcon />}{' '}
                    {c('upload_files')}
                </Button>
            )}
        </div>
    );
}
